import {
  useDeleteWasteManagementData,
  usePostWasteManagementData,
  usePutWasteManagementData
} from "../../../../../api/wasteManagement";
import { IWasteRecord } from "../../../../../components/wasteManagment/wasteManagement.types";
import {
  buildWasteApiPayload,
  normalizeWasteUnit
} from "../../../../../components/wasteManagment/wasteManagement.utils";
import {
  validateMoistureRange,
  validateWasteStreamName
} from "../../../../../components/wasteManagment/wasteManagement.validation";
import { MEASUREMENT_TYPES } from "../../../../../config/enums/MeasurementTypes";
import { errorView, successMessage } from "../../../../../helpers/ErrorHandler";
import { WasteRecord } from "../../../../../types/process/process.types";
import { GateKind } from "../types/processDetail.types";

interface UseProcessWasteCrudParams {
  processId: string;
  gateKind: GateKind;
  ingredientNameMap: Record<string, string>;
  onSaved: () => void;
}

const findIngredientId = (name: string, ingredientNameMap: Record<string, string>): string | null => {
  const entry = Object.entries(ingredientNameMap).find(([, label]) => label === name);
  return entry ? entry[0] : null;
};

const toIWasteRecord = (record: Partial<WasteRecord>, ingredientNameMap: Record<string, string>): IWasteRecord => ({
  managementName: record.streamName ?? "",
  managementTypeId: record.managementType ?? "1",
  sideStreamsDistribution: record.distribution === "" || record.distribution == null ? "" : String(record.distribution),
  unit: normalizeWasteUnit(record.distributionUnit, MEASUREMENT_TYPES.WEIGHT_KG),
  moistureContent: record.moisture === "" || record.moisture == null ? "" : String(record.moisture),
  ingredientId: record.ingredientName ? findIngredientId(record.ingredientName, ingredientNameMap) : null
});

const validateWasteRecord = (record: IWasteRecord): string | null => {
  if (!record.managementName || record.managementName.trim() === "") return "Stream name is required!";
  const streamNameValidation = validateWasteStreamName(record.managementName);
  if (streamNameValidation !== true) return streamNameValidation;

  const moistureValidation = validateMoistureRange(record.moistureContent);
  if (moistureValidation !== true) return moistureValidation;

  return null;
};

/** Waste-management CRUD against the dedicated processing-waste-management endpoints. */
export const useProcessWasteCrud = ({ processId, gateKind, ingredientNameMap, onSaved }: UseProcessWasteCrudParams) => {
  const type = gateKind === "farm" ? "farm" : "process";
  const supplierDataHandle = false;

  const { mutate: postWaste } = usePostWasteManagementData();
  const { mutate: putWaste } = usePutWasteManagementData();
  const { mutate: deleteWaste } = useDeleteWasteManagementData();

  const addWaste = (initial: Partial<WasteRecord> = {}) => {
    const record = toIWasteRecord(initial, ingredientNameMap);
    const validationError = validateWasteRecord(record);
    if (validationError) {
      errorView(validationError);
      return;
    }
    postWaste(
      { processingId: processId, type, supplierDataHandle, wasteData: buildWasteApiPayload(record) },
      {
        onSuccess: () => {
          successMessage("Waste record added");
          onSaved();
        },
        onError: (error: any) => errorView(error?.msg || "Failed to add waste record")
      }
    );
  };

  const updateWaste = (id: string, patch: Partial<WasteRecord>, current: WasteRecord) => {
    const record = toIWasteRecord({ ...current, ...patch }, ingredientNameMap);
    const validationError = validateWasteRecord(record);
    if (validationError) {
      errorView(validationError);
      return;
    }
    putWaste(
      { wasteManagementId: id, type, supplierDataHandle, wasteData: buildWasteApiPayload(record) },
      {
        onSuccess: () => {
          successMessage("Waste record updated");
          onSaved();
        },
        onError: (error: any) => errorView(error?.msg || "Failed to update waste record")
      }
    );
  };

  const removeWaste = (id: string) => {
    deleteWaste(
      { type, supplierDataHandle, wasteData: { wasteManagementId: id, processingId: processId } },
      {
        onSuccess: () => {
          successMessage("Waste record removed");
          onSaved();
        },
        onError: (error: any) => errorView(error?.msg || "Failed to remove waste record")
      }
    );
  };

  return { addWaste, updateWaste, removeWaste };
};
